'use client'

import { useParams } from 'next/navigation'
import { useEffect, useState } from 'react'

// Import base Noto Sans (already loaded through fonts.ts)
// Import additional script-specific fonts with optimized loading
const fontModules = {
  ar: () => import('@fontsource-variable/noto-sans-arabic'),
  zh: () => import('@fontsource-variable/noto-sans-sc'),
  'zh-TW': () => import('@fontsource-variable/noto-sans-tc'),
  ja: () => import('@fontsource-variable/noto-sans-jp'),
  ko: () => import('@fontsource-variable/noto-sans-kr'),
  he: () => import('@fontsource-variable/noto-sans-hebrew')
  // Add more language-specific fonts as needed
}

// Font loading priorities for different locales
const fontPriorities = {
  ar: 'high', // Arabic requires immediate font loading
  zh: 'high', // Chinese characters need specific fonts
  'zh-TW': 'high',
  ja: 'high', // Japanese requires specific fonts
  ko: 'high', // Korean requires specific fonts
  he: 'medium', // Hebrew can use fallbacks initially
  // Latin-based languages use 'low' priority as they have good fallbacks
}

type LocaleCodes = keyof typeof fontModules
type ParamsWithLocale = { locale: string }

export default function LocaleFonts() {
  const params = useParams<ParamsWithLocale>()
  const locale = params.locale
  const [fontLoaded, setFontLoaded] = useState(false)
  
  useEffect(() => {
    // Get primary language code from locale (e.g., 'zh-TW' -> 'zh')
    const primaryLanguage = locale.split('-')[0] as LocaleCodes
    const priority = fontPriorities[locale as LocaleCodes] || fontPriorities[primaryLanguage] || 'low'
    
    // Function to load font with timeout and error handling
    const loadFont = async (fontLoader: () => Promise<unknown>) => {
      try {
        // Add timeout for font loading to prevent blocking
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Font loading timeout')), 3000)
        )
        
        await Promise.race([fontLoader(), timeoutPromise])
        setFontLoaded(true)
      } catch (error) {
        console.warn(`Failed to load locale-specific font for ${locale}:`, error)
        // Gracefully degrade to system fonts
        setFontLoaded(true)
      }
    }
    
    // Load fonts based on priority
    if (priority === 'high') {
      // Load immediately for critical scripts
      if (locale in fontModules) {
        loadFont(fontModules[locale as LocaleCodes])
      } else if (primaryLanguage in fontModules) {
        loadFont(fontModules[primaryLanguage])
      } else {
        setFontLoaded(true)
      }
    } else if (priority === 'medium') {
      // Load after a short delay to not block critical rendering
      setTimeout(() => {
        if (locale in fontModules) {
          loadFont(fontModules[locale as LocaleCodes])
        } else if (primaryLanguage in fontModules) {
          loadFont(fontModules[primaryLanguage])
        } else {
          setFontLoaded(true)
        }
      }, 100)
    } else {
      // Low priority - load after page is interactive
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          if (locale in fontModules) {
            loadFont(fontModules[locale as LocaleCodes])
          } else if (primaryLanguage in fontModules) {
            loadFont(fontModules[primaryLanguage])
          } else {
            setFontLoaded(true)
          }
        })
      } else {
        // Fallback for browsers without requestIdleCallback
        setTimeout(() => {
          if (locale in fontModules) {
            loadFont(fontModules[locale as LocaleCodes])
          } else if (primaryLanguage in fontModules) {
            loadFont(fontModules[primaryLanguage])
          } else {
            setFontLoaded(true)
          }
        }, 500)
      }
    }
  }, [locale])
  
  // Add a class to document when fonts are loaded for CSS transitions
  useEffect(() => {
    if (fontLoaded) {
      document.documentElement.classList.add('fonts-loaded')
    }
  }, [fontLoaded])
  
  // This component doesn't render anything
  return null
} 